import {FC, ReactNode, useCallback, useEffect, useState} from "react";
import {Collapse} from "@mui/material";
import {styled} from "@mui/material/styles";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuItem from "./MenuItem";
import {t} from "../../../styles/theme/tokens";
import {MENU_COLLAPSE_STORAGE_KEY} from "./constants";

/**
 * MenuCollapseGroup — сворачиваемая группа пунктов меню.
 *
 * Триггер — это сам MenuItem (с заголовком группы как label, иконкой
 * группы слева и chevron в endAdornment). Стиль и высота — те же, что
 * у обычных пунктов, поэтому визуально иерархия читается естественно.
 *
 * Иконка триггера передаётся СНАРУЖИ (через props icon / iconActive),
 * не хардкодится. Это позволяет в menuConfig задавать разные иконки
 * для разных collapsible-групп (если их будет больше одной).
 *
 * Автораскрытие:
 *   - Если внутри есть активный пункт (forceOpen=true) — группа гарантированно
 *     открыта, и пользователь не может её закрыть, пока внутри активен пункт.
 *     Без этого можно было бы оказаться в состоянии "выбран /clients,
 *     но группа Админ свёрнута и непонятно, где находишься".
 *
 * Persistence:
 *   - Состояние раскрытия сохраняется в localStorage по ключу
 *     `atom:menu:collapse:{id}`.
 *   - SSR-safe: useEffect для чтения, чтобы initial render не дёргал window.
 */

interface MenuCollapseGroupProps {
    /** id группы для localStorage и aria. */
    id: string;
    /** Заголовок-триггер. */
    title: string;
    /** Иконка триггера (outlined для свёрнутого состояния). */
    icon: ReactNode;
    /** Иконка триггера (filled для раскрытого состояния). Опционально. */
    iconActive?: ReactNode;
    /** Начальное состояние, если в localStorage ничего нет. */
    defaultOpen?: boolean;
    /** Если есть активный пункт внутри — группа принудительно открыта. */
    forceOpen?: boolean;
    children: ReactNode;
}

const Chevron = styled(ExpandMore, {
    shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>(({isOpen}) => ({
    transition: `transform ${t.duration.base}ms ${t.easing}`,
    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
    color: "inherit",
}));

const MenuCollapseGroup: FC<MenuCollapseGroupProps> = ({
                                                           id,
                                                           title,
                                                           icon,
                                                           iconActive,
                                                           defaultOpen = false,
                                                           forceOpen = false,
                                                           children,
                                                       }) => {
    const storageKey = `${MENU_COLLAPSE_STORAGE_KEY}:${id}`;

    const [isOpenStored, setIsOpenStored] = useState<boolean>(defaultOpen);

    // Чтение из localStorage один раз на mount.
    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved !== null) setIsOpenStored(saved === "1");
        } catch {
            // ignore: localStorage может быть недоступен (SSR / privacy mode)
        }
    }, [storageKey]);

    const isOpen = forceOpen || isOpenStored;

    const handleToggle = useCallback(() => {
        // Если внутри есть активный пункт — не позволяем закрыть.
        // Без этого можно было бы оказаться в состоянии "выбран /clients,
        // но группа Админ свёрнута и непонятно, где находишься".
        if (forceOpen) return;

        const next = !isOpenStored;
        setIsOpenStored(next);
        try {
            localStorage.setItem(storageKey, next ? "1" : "0");
        } catch {
            // ignore
        }
    }, [forceOpen, isOpenStored, storageKey]);

    return (
        <>
            <MenuItem
                icon={icon}
                iconActive={iconActive}
                label={title}
                onClick={handleToggle}
                // isActive=true когда внутри есть активный пункт — даёт лёгкий
                // визуальный сигнал "вы внутри этой группы".
                isActive={forceOpen}
                ariaExpanded={isOpen}
                endAdornment={<Chevron isOpen={isOpen}/>}
            />
            <Collapse in={isOpen} timeout={{enter: 220, exit: 180}} unmountOnExit>
                <div role="group" aria-label={title}>
                    {children}
                </div>
            </Collapse>
        </>
    );
};

export default MenuCollapseGroup;